import React, { useEffect, useMemo, useRef, useState } from "react";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import "./admin-calendar.css";
import { useAdminAuth } from "./AuthContext";
import { useToast } from "../hooks/use-toast";
import { Loader2 } from "lucide-react";
import BookingActionModal from "./BookingActionModal";
import BookingEditModal from "./BookingEditModal";

const STATUS_COLORS = {
  requested: { bg: "#fde68a", border: "#f59e0b", text: "#78350f" }, // amber
  confirmed: { bg: "#a7f3d0", border: "#10b981", text: "#064e3b" }, // emerald
  declined:  { bg: "#fecaca", border: "#ef4444", text: "#7f1d1d" }, // rose
  completed: { bg: "#e2e8f0", border: "#64748b", text: "#1e293b" }, // slate
  cancelled: { bg: "#e5e7eb", border: "#9ca3af", text: "#374151" }, // gray
};

const DEFAULT_COLOR = { bg: "#bae6fd", border: "#0284c7", text: "#0c4a6e" };

function bookingToEvent(b) {
  const c = STATUS_COLORS[b.status] || DEFAULT_COLOR;
  const start = `${b.date}T${b.time || "09:00"}`;
  let end;
  if (b.time_to) {
    end = `${b.date}T${b.time_to}`;
  } else {
    const [hh, mm] = (b.time || "09:00").split(":").map((n) => parseInt(n, 10));
    const endHH = ((hh || 0) + 1).toString().padStart(2, "0");
    end = `${b.date}T${endHH}:${String(mm || 0).padStart(2, "0")}`;
  }
  return {
    id: b.id,
    title: `${b.name} · ${b.service_title}`,
    start,
    end,
    backgroundColor: c.bg,
    borderColor: c.border,
    textColor: c.text,
    extendedProps: { booking: b },
  };
}

export default function AdminCalendar() {
  const { authAxios } = useAdminAuth();
  const { toast } = useToast();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [modal, setModal] = useState({ open: false, action: null });
  const [editOpen, setEditOpen] = useState(false);
  // Pending drop info that we'll commit through the modal (so user can edit SMS / cancel)
  const pendingDropRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await authAxios.get("/bookings");
      setBookings(r.data || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const events = useMemo(() => bookings.map(bookingToEvent), [bookings]);

  // Per-date booking count for header pills
  const visitsByDate = useMemo(() => {
    const map = {};
    bookings.forEach((b) => {
      if (!b.date) return;
      map[b.date] = (map[b.date] || 0) + 1;
    });
    return map;
  }, [bookings]);

  const renderDayHeader = (arg) => {
    const d = arg.date;
    const viewType = arg.view?.type || "";
    const isMonthView = viewType.startsWith("dayGrid");
    const weekday = d.toLocaleDateString(undefined, { weekday: "short" });
    const isToday = arg.isToday;

    // Month view: just the weekday name (day numbers + visits live inside each cell)
    if (isMonthView) {
      return (
        <div className={`cpg-month-col-header ${isToday ? "is-today" : ""}`}>
          {weekday}
        </div>
      );
    }

    // Week / Day view: weekday + day number + visits badge
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    const key = `${yyyy}-${mm}-${dd}`;
    const visits = visitsByDate[key] || 0;
    const dayNum = d.getDate();
    return (
      <div className={`cpg-day-header ${isToday ? "is-today" : ""}`}>
        <div className="cpg-day-header-label">
          {weekday} <span className="cpg-day-header-num">{dayNum}</span>
        </div>
        <span className="cpg-day-header-visits">{visits} visit{visits === 1 ? "" : "s"}</span>
      </div>
    );
  };

  // Month-view day cells: render the day number + visits pill inline (top-left of each cell).
  // Only applies to dayGridMonth — in timeGrid views FullCalendar reuses this hook for the
  // column area which would otherwise create a duplicated day-number under the header.
  const renderDayCellContent = (arg) => {
    const viewType = arg.view?.type || "";
    if (!viewType.startsWith("dayGrid")) return undefined;
    const d = arg.date;
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    const key = `${yyyy}-${mm}-${dd}`;
    const visits = visitsByDate[key] || 0;
    const dayNum = d.getDate();
    return (
      <div className="cpg-month-daycell">
        <span className={`cpg-month-daynum ${arg.isToday ? "is-today" : ""}`}>{dayNum}</span>
        {visits > 0 && (
          <span className="cpg-month-visits">
            {visits} visit{visits === 1 ? "" : "s"}
          </span>
        )}
      </div>
    );
  };

  const handleEventClick = (info) => {
    const b = info.event.extendedProps.booking;
    setSelected(b);
    setEditOpen(true);
  };

  // When user drags an event onto a new slot
  const handleEventDrop = (info) => {
    const b = info.event.extendedProps.booking;
    const newStart = info.event.start;
    const newEnd = info.event.end;
    if (!newStart) {
      info.revert();
      return;
    }
    const fmt = (d) => {
      const hh = String(d.getHours()).padStart(2, "0");
      const mi = String(d.getMinutes()).padStart(2, "0");
      return `${hh}:${mi}`;
    };
    const yyyy = newStart.getFullYear();
    const mm = String(newStart.getMonth() + 1).padStart(2, "0");
    const dd = String(newStart.getDate()).padStart(2, "0");
    const newDate = `${yyyy}-${mm}-${dd}`;
    const newTime = fmt(newStart);
    const newTimeTo = newEnd ? fmt(newEnd) : "";

    pendingDropRef.current = { info, newDate, newTime, newTimeTo };
    setSelected({ ...b, date: newDate, time: newTime, time_to: newTimeTo });
    setModal({ open: true, action: "reschedule" });
  };

  const handleActionCompleted = (updated) => {
    pendingDropRef.current = null;
    setBookings((arr) => arr.map((b) => (b.id === updated.id ? updated : b)));
    setSelected(updated);
  };

  const handleBookingDeleted = (id) => {
    setBookings((arr) => arr.filter((b) => b.id !== id));
    setSelected(null);
  };

  const handleModalClose = (v) => {
    setModal((m) => ({ ...m, open: v }));
    // If modal closed without completing a drag-reschedule, revert the event
    if (!v && pendingDropRef.current) {
      pendingDropRef.current.info?.revert?.();
      pendingDropRef.current = null;
      toast({ title: "Reschedule cancelled" });
    }
  };

  return (
    <div className="p-8" data-testid="admin-calendar-page">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl font-extrabold text-slate-900">Calendar</h1>
          <p className="text-sm text-slate-500">Drag bookings to reschedule. Click to confirm or edit.</p>
        </div>
        <div className="flex items-center gap-3 text-xs">
          <Legend color={STATUS_COLORS.requested.bg} label="Requested" />
          <Legend color={STATUS_COLORS.confirmed.bg} label="Confirmed" />
          <Legend color={STATUS_COLORS.declined.bg} label="Declined" />
          <Legend color={STATUS_COLORS.completed.bg} label="Completed" />
        </div>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-slate-500"><Loader2 className="animate-spin" size={16} /> Loading…</div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4 cpg-calendar" data-testid="fc-calendar-container">
          <FullCalendar
            plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
            initialView="timeGridWeek"
            headerToolbar={{
              left: "prev,next today",
              center: "title",
              right: "dayGridMonth,timeGridWeek,timeGridDay",
            }}
            buttonText={{
              today: "Today",
              month: "Month",
              week: "Week",
              day: "Day",
            }}
            events={events}
            editable={true}
            droppable={false}
            eventDurationEditable={false}
            slotMinTime="06:00:00"
            slotMaxTime="21:00:00"
            slotDuration="00:30:00"
            slotLabelInterval="01:00:00"
            slotLabelFormat={{ hour: "numeric", minute: "2-digit", hour12: true }}
            eventTimeFormat={{ hour: "numeric", minute: "2-digit", hour12: true }}
            dayHeaderContent={renderDayHeader}
            dayCellContent={renderDayCellContent}
            allDaySlot={false}
            nowIndicator={true}
            firstDay={1}
            expandRows={true}
            stickyHeaderDates={true}
            handleWindowResize={true}
            slotEventOverlap={false}
            eventDisplay="block"
            height="80vh"
            scrollTime="07:00:00"
            eventClick={handleEventClick}
            eventDrop={handleEventDrop}
            timeZone="local"
          />
        </div>
      )}

      <BookingActionModal
        open={modal.open}
        action={modal.action}
        booking={selected}
        onOpenChange={handleModalClose}
        onCompleted={handleActionCompleted}
      />

      <BookingEditModal
        open={editOpen}
        onOpenChange={setEditOpen}
        booking={selected}
        onSaved={handleActionCompleted}
        onDeleted={handleBookingDeleted}
      />
    </div>
  );
}

function Legend({ color, label }) {
  return (
    <div className="flex items-center gap-1.5 text-slate-600">
      <span className="w-3 h-3 rounded-sm" style={{ background: color }} />
      {label}
    </div>
  );
}
